/**
 * StationsBoard: a card per kitchen station (ADR 0027): name and type, primary and fallback printer with their state
 * and when they last answered, queued and failed print jobs, tickets today and the last ticket's time.
 *
 * Props: `mode`. At the store it reloads every 15 seconds while the tab is visible and a printer's *Test print*
 * (`store.devices.manage`) shows its result in a notification. At HO a store is picked first (the API needs one), the
 * data is labelled "Finished orders only, may lag", and there are no printers and no actions.
 */
import { Button, InlineNotification, Link as CarbonLink, SkeletonText } from '@carbon/react';
import { Printer } from '@carbon/icons-react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAdmin } from '../adminContext';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useFormat } from '../orders/model';
import { StatTile } from '../overview/StatTile';
import { WidgetCard } from '../overview/WidgetCard';
import { LagNotice } from '../overview/widgets';
import { todayRange } from '../overview/overviewApi';
import { asProblem, problemMessage } from '../problems';
import { hasPermission } from '../session';
import type { DeviceHealth, PrintJob, PrinterStatus, StationSummary } from './kitchenApi';
import { KITCHEN_DEVICES_PERMISSION, KITCHEN_POLL_MS, kitchenKey, useKitchenApi, type KitchenMode } from './model';
import { StorePicker } from './StorePicker';
import { KitchenStatusTag } from './KitchenStatusTag';
import './kitchen.scss';

const NOTICE_MS = 8000;

type TestResult = { kind: 'success' | 'error'; title: string; subtitle?: string };

export function StationsBoard({ mode }: { mode: KitchenMode }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const api = useKitchenApi();
  const queryClient = useQueryClient();
  const [storeId, setStoreId] = useState('');
  const [result, setResult] = useState<TestResult>();
  const canTest = mode === 'store' && hasPermission(session, KITCHEN_DEVICES_PERMISSION);
  const enabled = mode === 'store' || storeId !== '';
  const refetchInterval = mode === 'store' ? KITCHEN_POLL_MS : false;

  const stations = useQuery({
    queryKey: [...kitchenKey, 'stations', storeId],
    queryFn: () => api.stations(storeId || undefined),
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
  // The printers' own status is fresher than the station summary; without it the summary's copy is shown.
  const printers = useQuery({
    queryKey: [...kitchenKey, 'printers'],
    queryFn: () => api.printers(),
    enabled: mode === 'store',
    refetchInterval,
    refetchIntervalInBackground: false,
  });
  const lastTickets = useQuery({
    queryKey: [...kitchenKey, 'last-tickets', mode, storeId],
    queryFn: async () => {
      const page = await api.tickets({ ...todayRange(), storeId: storeId || undefined, page: 1, pageSize: 100 });
      const latest = new Map<string, string>();
      for (const ticket of page.items) if (!latest.has(ticket.stationId)) latest.set(ticket.stationId, ticket.firedAt);
      return latest;
    },
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (result?.kind !== 'success') return undefined;
    const timer = setTimeout(() => setResult(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [result]);

  const test = useMutation({
    mutationFn: (device: DeviceHealth) => api.testPrint(device.deviceId),
    onSuccess: (job: PrintJob, device) => {
      setResult(
        job.status === 'printed'
          ? { kind: 'success', title: t('admin.kitchen.board.testPrinted', { name: device.name }) }
          : {
              kind: 'error',
              title: t('admin.kitchen.board.testFailed', { name: device.name }),
              subtitle: job.lastError ?? undefined,
            },
      );
      void queryClient.invalidateQueries({ queryKey: [...kitchenKey, 'printers'] });
      void queryClient.invalidateQueries({ queryKey: [...kitchenKey, 'stations'] });
    },
    onError: (error, device) =>
      setResult({
        kind: 'error',
        title: t('admin.kitchen.board.testFailed', { name: device.name }),
        subtitle: problemMessage(t, asProblem(error)),
      }),
  });

  const status = new Map<string, PrinterStatus>((printers.data ?? []).map((p) => [p.deviceId, p]));
  const rows = stations.data ?? [];

  return (
    <section className="pos-admin-page">
      <PageHeader
        title={t('admin.kitchen.board.title')}
        description={t(mode === 'ho' ? 'admin.kitchen.board.hoHint' : 'admin.kitchen.board.hint')}
        actions={mode === 'ho' ? <LagNotice /> : undefined}
      />
      {mode === 'ho' && (
        <div className="pos-kitchen__form">
          <StorePicker value={storeId} onChange={setStoreId} />
        </div>
      )}
      {result && (
        <InlineNotification
          kind={result.kind}
          role={result.kind === 'error' ? 'alert' : 'status'}
          lowContrast
          hideCloseButton
          title={result.title}
          subtitle={result.subtitle}
        />
      )}
      {!enabled ? (
        <EmptyState heading={t('admin.kitchen.board.pickStore')} description={t('admin.kitchen.board.pickStoreHint')} />
      ) : stations.isError ? (
        <WidgetCard
          title={t('admin.kitchen.board.title')}
          isError
          error={stations.error}
          onRetry={() => void stations.refetch()}
        />
      ) : stations.isPending ? (
        <div className="pos-kitchen__stations">
          <WidgetCard title={<SkeletonText width="8rem" />} isLoading />
          <WidgetCard title={<SkeletonText width="8rem" />} isLoading />
        </div>
      ) : rows.length === 0 ? (
        <WidgetCard title={t('admin.kitchen.board.title')} isEmpty emptyHeading={t('admin.kitchen.board.empty')} emptyDescription={t('admin.kitchen.board.emptyHint')} />
      ) : (
        <div className="pos-kitchen__stations">
          {rows.map((station) => (
            <StationCard
              key={station.stationId}
              station={station}
              mode={mode}
              status={status}
              lastTicket={lastTickets.data?.get(station.stationId)}
              canTest={canTest}
              testing={test.isPending ? test.variables?.deviceId : undefined}
              onTest={(device) => test.mutate(device)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function StationCard({
  station,
  mode,
  status,
  lastTicket,
  canTest,
  testing,
  onTest,
}: {
  station: StationSummary;
  mode: KitchenMode;
  status: Map<string, PrinterStatus>;
  lastTicket: string | undefined;
  canTest: boolean;
  testing: string | undefined;
  onTest(device: DeviceHealth): void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const failed = Number(station.failedJobs ?? 0);
  return (
    <WidgetCard
      title={`${station.code} · ${station.name}`}
      headerExtra={<span>{t(`admin.kitchen.stationType.${station.stationType}` as I18nKey, { defaultValue: station.stationType })}</span>}
    >
      <div className="pos-kitchen__card-body">
        {mode === 'store' && (
          <>
            <PrinterLine label={t('admin.kitchen.board.primary')} device={station.primary} status={status} canTest={canTest} testing={testing} onTest={onTest} />
            <PrinterLine label={t('admin.kitchen.board.fallback')} device={station.fallback} status={status} canTest={canTest} testing={testing} onTest={onTest} />
          </>
        )}
        <div className="pos-kitchen__stats">
          {mode === 'store' && (
            <>
              <StatTile label={t('admin.kitchen.board.queued')} value={format.number(station.queuedJobs ?? 0)} />
              <StatTile
                label={t('admin.kitchen.board.failed')}
                value={format.number(failed)}
                href={failed > 0 ? '/kitchen/print-queue?status=failed' : undefined}
              />
            </>
          )}
          <StatTile label={t('admin.kitchen.board.ticketsToday')} value={format.number(station.ticketsToday)} />
          <StatTile
            label={t('admin.kitchen.board.lastTicket')}
            value={lastTicket ? format.time(lastTicket) : t('admin.kitchen.board.none')}
          />
        </div>
        {mode === 'store' && Number(station.ticketsNotPrinted) > 0 && (
          <CarbonLink as={Link} to="/kitchen/tickets">
            {t('admin.kitchen.board.notPrinted', { count: Number(station.ticketsNotPrinted) })}
          </CarbonLink>
        )}
      </div>
    </WidgetCard>
  );
}

/** One printer line: name, state, last seen, and its Test print action. */
function PrinterLine({
  label,
  device,
  status,
  canTest,
  testing,
  onTest,
}: {
  label: string;
  device: DeviceHealth | null;
  status: Map<string, PrinterStatus>;
  canTest: boolean;
  testing: string | undefined;
  onTest(device: DeviceHealth): void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  if (!device) {
    return (
      <p className="pos-kitchen__muted">
        {label}: {t('admin.kitchen.board.noPrinter')}
      </p>
    );
  }
  const live = status.get(device.deviceId);
  const state = live?.state ?? device.state;
  const seen = live?.lastSeenAt ?? device.lastSeenAt;
  return (
    <div className="pos-kitchen__printer">
      <span className="pos-kitchen__muted">{label}</span>
      <span className="pos-kitchen__printer-name">{device.name}</span>
      <KitchenStatusTag kind="printer" status={state} />
      <span className="pos-kitchen__muted">
        {seen ? t('admin.kitchen.board.lastSeen', { time: format.dateTime(seen) }) : t('admin.kitchen.board.neverSeen')}
      </span>
      {canTest && (
        <Button
          kind="tertiary"
          renderIcon={Printer}
          disabled={testing === device.deviceId}
          aria-label={t('admin.rowAction', { action: t('admin.kitchen.board.testPrint'), record: device.name })}
          onClick={() => onTest(device)}
        >
          {t('admin.kitchen.board.testPrint')}
        </Button>
      )}
    </div>
  );
}
